/**
 * A call, from the first ring to the last second.
 *
 * The desktop's `CallWindow` in a browser: one component, five states, and only
 * the words and the buttons differ between them. What it is not is a second
 * place that knows about media — it is handed a `CallSession`, it renders what
 * that session reports and it emits what the person pressed. No `getUserMedia`,
 * no SFU, no track plumbing; swapping the stub for the real session changes
 * `openCallSession` and nothing in this file.
 *
 * Which controls exist in which state is `controls.ts`, not a chain of
 * conditions in the bar — see that file for why.
 */

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { participantLabel } from '../../lib/relay/types';
import type { CallSession, DeviceList, RemotePeer } from '../../lib/calls/types';
import { controlsFor, hangUpKey, mediaFailureKey, speakerOf, statusKey, uiStateOf } from './controls';
import { useCallKeys } from './keys';
import './call.css';

export interface CallOverlayProps {
  session: CallSession;
  /** Who placed the call. A ring you did not start is answered, not cancelled. */
  direction: 'incoming' | 'outgoing';
  /** The other end, for the states where there is no tile yet. */
  peerName: string;
  /** Answer and Decline exist only for an incoming call, so these two are only
   *  ever called from that state. */
  onAnswer?: () => void;
  onDecline?: () => void;
  onHangUp?: () => void;
}

/**
 * Everything the session reports, as React state.
 *
 * `CallSession` is an event emitter with mutable fields rather than an
 * immutable snapshot, so one counter re-reads the lot. A per-field diff here
 * would be a second model of the session's state and would disagree with it the
 * first time the real implementation changed a field without an event.
 */
export function useCallState(session: CallSession) {
  const [version, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const offs = [
      session.on('statusChanged', bump),
      session.on('rosterChanged', bump),
      session.on('speaking', bump),
      session.on('trackAdded', bump),
      session.on('trackRemoved', bump),
      session.on('recordingChanged', bump)
    ];
    return () => offs.forEach((off) => off());
  }, [session]);

  return useMemo(
    () => ({
      status: session.status,
      peers: session.peers(),
      local: session.local,
      version,
      /** Re-read the session. A local change — mute, camera, screen — is a
       *  mutation with no event behind it, so whoever makes one says so. */
      refresh: bump
    }),
    // `version` is the dependency: the fields it reads are mutable and React
    // cannot see them change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, version]
  );
}

/**
 * The call bar's tools are 44px discs.
 *
 * The app draws them at `chrome`'s 44 with a 22 radius — round, because a call's
 * controls have been round since the first phone — and the design system's
 * IconButton is a 6px chrome button sized for a toolbar. Passed as `style`
 * rather than set in `call.css`: the bundle writes its own box inline and
 * spreads the caller's `style` last, so this is the only thing a class would
 * have to fight with `!important`.
 */
const TOOL = { width: 44, height: 44, borderRadius: 'var(--radius-pill)' } as const;

export function CallOverlay({
  session,
  direction,
  peerName,
  onAnswer,
  onDecline,
  onHangUp
}: CallOverlayProps) {
  const { IconButton } = ds();
  const t = useT();
  const { status, peers, local, refresh } = useCallState(session);
  const [view, setView] = useState<'grid' | 'speaker'>('grid');
  const [toast, setToast] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const state = uiStateOf(status, direction, peers.length > 0);
  const controls = controlsFor(state);

  // Kept rather than read at the end, because the session reports the reason
  // and then tears itself down: by the time the state is `ended` the error is
  // gone.
  useEffect(
    () =>
      session.on('error', (error) => {
        const key = mediaFailureKey(error);
        if (key) setFailure(key);
      }),
    [session]
  );

  const { pushToTalk, setPushToTalkEnabled } = useCallKeys({
    session,
    enabled: state === 'active' || state === 'connecting',
    onMark: () => setToast(t('call.mark.posted')),
    onChange: refresh
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const speaker = speakerOf(peers);
  // Why it ended beats that it ended; every other state is its own line.
  const line = state === 'ended' && failure ? failure : statusKey(state);

  return (
    <div className="mv-call">
      <div className="mv-call-stage">
        {peers.length === 0 ? (
          <Waiting name={peerName} line={t(line)} />
        ) : view === 'speaker' && speaker ? (
          <div className="mv-call-tiles" style={{ gridTemplateColumns: '1fr', height: '100%' }}>
            <Tile peer={speaker} speaker />
          </div>
        ) : (
          <div className="mv-call-tiles">
            {peers.map((peer) => (
              <Tile key={peer.participantId} peer={peer} />
            ))}
          </div>
        )}
      </div>

      {/* Audio for everyone the speaker view is not showing. A tile's own
          <video> carries its camera; the microphone is a separate track and
          plays whether or not that peer is on screen. */}
      {peers.map((peer) => (
        <RemoteAudio key={`a-${peer.participantId}`} peer={peer} deafened={local.deafened} />
      ))}

      {state === 'active' && (
        <div className="mv-call-chips">
          <StatsChip session={session} />
          {pushToTalk.enabled && (
            <span className="mv-call-chip" data-live={pushToTalk.held ? 'true' : 'false'}>
              {pushToTalk.held ? t('call.ptt.talking') : t('call.ptt.hold')}
            </span>
          )}
          {peers.length > 1 && (
            <IconButton
              icon={view === 'grid' ? 'ScreenShare' : 'People'}
              label={view === 'grid' ? t('call.view.speaker') : t('call.view.grid')}
              size="sm"
              variant="ghost"
              onClick={() => setView(view === 'grid' ? 'speaker' : 'grid')}
            />
          )}
        </div>
      )}

      {/* An ended call has no controls, and the bar drew a second copy of the
          state line where they had been — the sentence the stage is already
          showing. Said once, and the strip goes with it rather than staying
          behind as an empty bordered row. */}
      {controls.length > 0 && (
        <div className="mv-call-bar">
          {controls.includes('decline') && (
            <Disc tone="danger" icon="HangUp" label={t('call.decline')} onClick={onDecline} />
          )}
          {controls.includes('answer') && (
            <Disc tone="answer" icon="Call" label={t('call.answer')} onClick={onAnswer} />
          )}

          {controls.includes('mute') && (
            <MicControl
              session={session}
              muted={local.muted}
              pushToTalkEnabled={pushToTalk.enabled}
              onPushToTalk={setPushToTalkEnabled}
              onChange={refresh}
            />
          )}
          {controls.includes('camera') && (
            <CameraControl session={session} on={local.cam} onChange={refresh} />
          )}
          {controls.includes('screen') && (
            <IconButton
              icon={local.screen ? 'ScreenShare' : 'ScreenStop'}
              label={t('call.screen')}
              active={local.screen}
              style={TOOL}
              onClick={() => void session.setScreen(!local.screen).then(refresh)}
            />
          )}
          {controls.includes('deafen') && (
            <IconButton
              icon={local.deafened ? 'SpeakerOff' : 'SpeakerOn'}
              label={t('call.deafen')}
              active={local.deafened}
              style={TOOL}
              onClick={() => {
                session.setDeafened(!local.deafened);
                refresh();
              }}
            />
          )}
          {controls.includes('hangUp') && (
            <Disc
              tone="danger"
              size="sm"
              icon="HangUp"
              label={t(hangUpKey(state))}
              onClick={() => {
                void session.leave();
                onHangUp?.();
              }}
            />
          )}
        </div>
      )}

      {toast && <CallToast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

/** The state line, and the name it belongs to. */
function Waiting({ name, line }: { name: string; line: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontFamily: 'var(--font-display), sans-serif',
          fontWeight: 600,
          fontSize: 24,
          lineHeight: 1.2
        }}
      >
        {name}
      </div>
      <div style={{ color: 'var(--text-muted)', marginTop: 'var(--space-4)' }}>{line}</div>
    </div>
  );
}

/**
 * One seat.
 *
 * The label is `participantLabel`, always — a component that formats its own is
 * the bug that has no symptom, and a stranger's name reads as an account's.
 */
function Tile({ peer, speaker }: { peer: RemotePeer; speaker?: boolean }) {
  const { Icon } = ds();
  const track = peer.tracks.screen ?? peer.tracks.cam;

  return (
    <div
      className="mv-call-tile"
      data-speaking={peer.speaking ? 'true' : 'false'}
      data-speaker={speaker ? 'true' : 'false'}
    >
      {track ? <TrackVideo track={track} muted /> : null}
      <span className="mv-call-name">
        {peer.muted && <Icon name="MicOff" size={14} />}
        {participantLabel({ displayName: peer.displayName, guest: peer.guest })}
      </span>
    </div>
  );
}

/** A track in a `<video>`. `srcObject` is a property, not an attribute, so it
 *  cannot be set in JSX and this effect is the whole reason the wrapper
 *  exists. */
function TrackVideo({ track, muted }: { track: MediaStreamTrack; muted?: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = new MediaStream([track]);
    return () => {
      el.srcObject = null;
    };
  }, [track]);

  return <video ref={ref} className="mv-call-video" autoPlay playsInline muted={muted} />;
}

/** Somebody else's microphone. Deafening silences it here rather than at the
 *  session, because deafening is this browser's own decision and the other end
 *  is not told. */
function RemoteAudio({ peer, deafened }: { peer: RemotePeer; deafened: boolean }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const track = peer.tracks.mic;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = track ? new MediaStream([track]) : null;
    return () => {
      el.srcObject = null;
    };
  }, [track]);

  if (!track) return null;
  return <audio ref={ref} autoPlay muted={deafened} />;
}

function Disc({
  tone,
  icon,
  label,
  size,
  onClick
}: {
  tone: 'answer' | 'danger';
  icon: string;
  label: string;
  size?: 'sm';
  onClick?: () => void;
}) {
  const { Icon } = ds();
  return (
    <button
      type="button"
      className="mv-call-disc"
      data-tone={tone}
      data-size={size}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icon name={icon} size={22} color="currentColor" />
    </button>
  );
}

/** The microphone, its devices and push to talk. */
function MicControl({
  session,
  muted,
  pushToTalkEnabled,
  onPushToTalk,
  onChange
}: {
  session: CallSession;
  muted: boolean;
  pushToTalkEnabled: boolean;
  onPushToTalk: (on: boolean) => void;
  onChange: () => void;
}) {
  const { IconButton, Switch } = ds();
  const t = useT();
  const [open, setOpen] = useState(false);
  const devices = useDevices(session, open);

  return (
    <span className="mv-call-control">
      <IconButton
        icon={muted ? 'MicOff' : 'MicOn'}
        label={muted ? t('call.unmute') : t('call.mute')}
        active={muted}
        style={TOOL}
        onClick={() => {
          session.setMuted(!muted);
          onChange();
        }}
      />
      <IconButton
        icon="CaretDown"
        label={t('call.devices.mic')}
        size="sm"
        variant="ghost"
        onClick={() => setOpen(!open)}
      />
      {open && (
        <Menu onClose={() => setOpen(false)}>
          {/* A session that cannot switch microphones mid-call does not list
              them: a row that does nothing when pressed is worse than no row. */}
          {session.setMicrophone && (
            <>
              <div className="mv-call-menu-label">{t('call.devices.mic')}</div>
              <DeviceRows
                devices={devices.mics}
                empty={t('call.devices.none')}
                onPick={(id) => void session.setMicrophone?.(id)}
              />
            </>
          )}
          <div className="mv-call-menu-label">{t('call.devices.speaker')}</div>
          <DeviceRows
            devices={devices.speakers}
            empty={t('call.devices.none')}
            onPick={(id) => void session.setSpeaker(id)}
          />
          <div style={{ padding: 'var(--space-8) var(--space-10)' }}>
            <Switch
              label={t('call.ptt.label')}
              checked={pushToTalkEnabled}
              onChange={(on) => onPushToTalk(on)}
            />
          </div>
        </Menu>
      )}
    </span>
  );
}

/** The camera, its devices and the background picker. */
function CameraControl({
  session,
  on,
  onChange
}: {
  session: CallSession;
  on: boolean;
  onChange: () => void;
}) {
  const { IconButton, Button } = ds();
  const t = useT();
  const [open, setOpen] = useState(false);
  const devices = useDevices(session, open);
  const file = useRef<HTMLInputElement | null>(null);

  return (
    <span className="mv-call-control">
      <IconButton
        icon={on ? 'CameraOn' : 'CameraOff'}
        label={t('call.camera')}
        active={on}
        style={TOOL}
        onClick={() => void session.setCamera(!on).then(onChange)}
      />
      <IconButton
        icon="CaretDown"
        label={t('call.devices.camera')}
        size="sm"
        variant="ghost"
        onClick={() => setOpen(!open)}
      />
      {open && (
        <Menu onClose={() => setOpen(false)}>
          <div className="mv-call-menu-label">{t('call.devices.camera')}</div>
          <DeviceRows
            devices={devices.cams}
            empty={t('call.devices.none')}
            onPick={(id) => void session.setCamera(true, id).then(onChange)}
          />
          <div className="mv-call-menu-label">{t('call.background.title')}</div>
          <button
            type="button"
            className="mv-call-menu-item"
            onClick={() => void session.setBackground({ kind: 'none' })}
          >
            {t('call.background.none')}
          </button>
          <button
            type="button"
            className="mv-call-menu-item"
            onClick={() => void session.setBackground({ kind: 'blur' })}
          >
            {t('call.background.blur')}
          </button>
          <div style={{ padding: 'var(--space-8) var(--space-10)' }}>
            <Button size="sm" variant="secondary" onClick={() => file.current?.click()}>
              {t('call.background.image')}
            </Button>
            {/* A blob URL, which is what the CSP's `media-src blob:` is for.
                Nothing is uploaded — the picture never leaves this browser. */}
            <input
              ref={file}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (!picked) return;
                void session.setBackground({
                  kind: 'image',
                  url: URL.createObjectURL(picked)
                });
              }}
            />
          </div>
        </Menu>
      )}
    </span>
  );
}

function DeviceRows({
  devices,
  empty,
  onPick
}: {
  devices: MediaDeviceInfo[];
  empty: string;
  onPick: (deviceId: string) => void;
}) {
  // A device with no permission behind it answers with an empty id and an empty
  // label: several of them share a key, and none of them is a row anybody could
  // pick. "No devices." is the honest line for a list of those.
  const pickable = devices.filter((d) => d.deviceId);

  if (pickable.length === 0) {
    return (
      <div style={{ padding: 'var(--space-8) var(--space-10)', color: 'var(--text-muted)' }}>
        {empty}
      </div>
    );
  }
  return (
    <>
      {pickable.map((device) => (
        <button
          key={device.deviceId}
          type="button"
          className="mv-call-menu-item"
          onClick={() => onPick(device.deviceId)}
        >
          {device.label || device.deviceId}
        </button>
      ))}
    </>
  );
}

const NO_DEVICES: DeviceList = { mics: [], cams: [], speakers: [] };

/** Asked for when the menu opens, not on mount: `enumerateDevices` answers with
 *  nameless entries until a permission exists, and a list read too early is a
 *  list of blanks that never refreshes. */
function useDevices(session: CallSession, open: boolean): DeviceList {
  const [devices, setDevices] = useState<DeviceList>(NO_DEVICES);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void session.devices().then((list) => {
      if (!cancelled) setDevices(list);
    });
    return () => {
      cancelled = true;
    };
  }, [session, open]);

  return devices;
}

/** A menu that closes on a click elsewhere and on Escape. Escape closes the
 *  menu and does nothing else — it never leaves the call. */
function Menu({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="mv-call-menu" ref={ref} role="menu">
      {children}
    </div>
  );
}

/** Round trip and loss, every three seconds — the same cadence as the roster
 *  poll. One line of mono, and no reading of it: a number beside a word is a
 *  connection indicator, a sentence about it is an apology. */
export function StatsChip({ session }: { session: CallSession }) {
  const t = useT();
  const [line, setLine] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const read = () => {
      void session.stats().then((stats) => {
        if (cancelled) return;
        setLine(
          `${Math.round(stats.rttMs)} ms · ${stats.packetLossPercent.toFixed(1)}% · ${Math.round(
            stats.outboundKbps
          )} kbps`
        );
      });
    };
    read();
    const timer = window.setInterval(read, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [session]);

  return (
    <span className="mv-call-chip" title={t('call.stats')}>
      {line ?? t('call.stats')}
    </span>
  );
}

/** The design system's Toast, floating. It carries a title and nothing else —
 *  a mark posted needs no second sentence. */
export function CallToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  const { Toast } = ds();
  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'var(--space-24)',
        transform: 'translateX(-50%)',
        zIndex: 40
      }}
    >
      <Toast title={message} onDismiss={onDismiss} />
    </div>
  );
}
